import {
  assistantScopes,
  getOAuthClient,
  giveConsent,
  listTrips,
  type AssistantScope,
  type Consent,
  type TripSummary,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ScopeChecks, TripsPick } from './Assistant';

/** Where the browser goes: the assistant's page, or back to it said no. */
export const go = {
  to: (address: string) => {
    window.location.assign(address);
  },
};

/** What an assistant asks by default when it names no scope: to read, and change the plan. */
const ASKED: AssistantScope[] = ['trips', 'plan', 'bookings'];

/** TripPath's own page where its person gives an assistant leave (`/oauth/authorize`). */
export function Authorize() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const asked: Consent = {
    clientId: params.get('client_id') ?? '',
    redirectUri: params.get('redirect_uri') ?? '',
    codeChallenge: params.get('code_challenge') ?? '',
    codeChallengeMethod: params.get('code_challenge_method') ?? '',
    state: params.get('state'),
  };
  const named = (params.get('scope') ?? '')
    .split(' ')
    .flatMap((word) => assistantScopes.filter((scope) => scope === word));
  const complete =
    asked.clientId !== '' && asked.redirectUri !== '' && asked.codeChallenge !== '';
  const [client, setClient] = useState<
    { kind: 'asking' } | { kind: 'unknown' } | { kind: 'named'; name: string }
  >({ kind: 'asking' });
  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [scopes, setScopes] = useState<AssistantScope[]>(named.length > 0 ? named : ASKED);
  const [trip, setTrip] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!complete) return;
    getOAuthClient(asked.clientId, asked.redirectUri).then(
      (name) => {
        setClient({ kind: 'named', name });
      },
      () => {
        setClient({ kind: 'unknown' });
      },
    );
    listTrips().then(setTrips, () => {
      setTrips([]);
    });
  }, [complete, asked.clientId, asked.redirectUri]);

  if (!complete || client.kind === 'unknown') {
    return (
      <div className="page assistant-ask">
        <p className="panel form-failed" role="alert">
          {t(complete ? 'assistant.ask.unknown' : 'assistant.ask.broken')}
        </p>
      </div>
    );
  }
  if (client.kind === 'asking') return <p className="page quiet">{t('trips.loading')}</p>;
  const deny = () => {
    const back = new URL(asked.redirectUri);
    back.searchParams.set('error', 'access_denied');
    if (asked.state !== null) back.searchParams.set('state', asked.state);
    go.to(back.toString());
  };
  return (
    <div className="page assistant-ask">
      <section className="panel" aria-labelledby="assistant-ask">
        <h1 id="assistant-ask">{t('assistant.ask.title', { name: client.name })}</h1>
        <p className="quiet">{t('assistant.ask.lead')}</p>
        <ScopeChecks chosen={scopes} onChange={setScopes} />
        <TripsPick trips={trips} chosen={trip} onChange={setTrip} />
        {failed && (
          <p className="form-failed" role="alert">
            {t('assistant.ask.failed')}
          </p>
        )}
        <div className="assistant-ask-actions">
          <button type="button" className="button button-quiet" onClick={deny}>
            {t('assistant.ask.deny')}
          </button>
          <button
            type="button"
            className="button button-primary"
            disabled={scopes.length === 0}
            onClick={() => {
              setFailed(false);
              giveConsent(asked, scopes, trip === '' ? null : [trip]).then(
                (redirect) => {
                  go.to(redirect);
                },
                () => {
                  setFailed(true);
                },
              );
            }}
          >
            {t('assistant.ask.allow')}
          </button>
        </div>
      </section>
    </div>
  );
}
